import { Link, useParams } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { useSportsArticles } from "@/hooks/useSportsArticles";
import { useRssAutoFetcher } from "@/hooks/useRssAutoFetcher";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { sportsCategories } from "@/mocks/sportsData";

const DEFAULT_FEATURED_IMAGE = "https://readdy.ai/api/search-image?query=Professional%20sports%20analysis%20scene%20with%20stadium%20atmosphere%2C%20tactical%20board%20overlay%2C%20modern%20data%20visualization%20elements%2C%20dark%20sophisticated%20sports%20broadcast%20aesthetic%2C%20dynamic%20composition%20with%20subtle%20motion%20blur%2C%20dramatic%20lighting%2C%20editorial%20sports%20photography%20style&width=1200&height=640&seq=sports-featured-02&orientation=landscape";

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function SportsCategoryPage() {
  const { sport } = useParams<{ sport: string }>();
  const { articles, loading, error, sportLabelMap } = useSportsArticles(sport);
  const { settings } = useSiteSettings();
  useRssAutoFetcher();

  const sportLabel = sport ? (sportLabelMap[sport] || sport) : "전체";
  const featuredImage = (sport && settings[`sports_featured_image_${sport}`]) || settings.sports_featured_image || DEFAULT_FEATURED_IMAGE;

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[1000px] mx-auto px-3 md:px-4 py-8 md:py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center text-primary-500">
              <i className="ri-bar-chart-box-line text-2xl" />
            </span>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-700">{sportLabel} 분석</h1>
              <p className="text-sm text-foreground-600 mt-0.5">
                {sportLabel} 종목의 전문 분석 정보를 확인하세요
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="max-w-[1000px] mx-auto px-3 md:px-4 py-6">
        <div className="flex items-center gap-2 mb-6 overflow-x-auto">
          <Link
            to="/sports"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-700"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-apps-line" />
            </span>
            전체
          </Link>
          {sportsCategories.map((cat) => (
            <Link
              key={cat.id}
              to={`/sports/${cat.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${
                sport === cat.id
                  ? "bg-primary-500 text-foreground-50"
                  : "bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-700"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={cat.icon} />
              </span>
              {cat.label}
            </Link>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="flex items-center gap-2 text-foreground-600">
              <span className="w-4 h-4 flex items-center justify-center animate-spin">
                <i className="ri-loader-4-line" />
              </span>
              <span className="text-sm">불러오는 중...</span>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                <i className="ri-error-warning-line text-xl" />
              </span>
            </div>
            <p className="text-sm text-foreground-600 mb-3">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-foreground-50 text-xs font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-refresh-line" />
              </span>
              다시 시도
            </button>
          </div>
        )}

        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                <i className="ri-inbox-line" />
              </span>
            </div>
            <p className="text-sm text-foreground-600">등록된 콘텐츠가 없습니다</p>
          </div>
        )}

        {!loading && !error && articles.length > 0 && (
          <>
            {/* Featured */}
            <Link
              to={`/sports/article/${articles[0].id}`}
              className="relative bg-background-100 rounded-xl border border-background-300 overflow-hidden mb-6 h-[280px] md:h-[320px] block cursor-pointer group"
            >
              <img
                alt=""
                src={featuredImage}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold bg-primary-500 text-foreground-50 rounded mb-2">
                  {sportLabelMap[articles[0].sport_type] || articles[0].sport_type}
                </span>
                <h2 className="text-lg md:text-xl font-bold text-white mb-1">{articles[0].title}</h2>
                <p className="text-xs text-white/70">
                  {articles[0].source} · 조회 {articles[0].views} · 좋아요 {articles[0].likes} · 댓글 {articles[0].comments} · {formatDate(articles[0].created_at)}
                </p>
              </div>
            </Link>

            {/* News Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {articles.slice(1).map((article) => (
                <Link
                  key={article.id}
                  to={`/sports/article/${article.id}`}
                  className="bg-background-100 rounded-lg border border-background-300 p-4 hover:border-primary-500/20 transition-colors block cursor-pointer"
                >
                  <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-secondary-500/10 text-secondary-400 rounded mb-2">
                    {sportLabelMap[article.sport_type] || article.sport_type}
                  </span>
                  <h3 className="text-sm font-bold text-foreground-700 mb-2 leading-snug">{article.title}</h3>
                  <div className="flex items-center gap-3 text-[11px] text-foreground-600 flex-wrap">
                    <span>{article.source}</span>
                    <span>조회 {article.views}</span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-thumb-up-line" /> {article.likes}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-chat-3-line" /> {article.comments}
                    </span>
                    <span>{formatDate(article.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}